﻿/**
 * Orchestrator Class
 * Handles postMessage communication between fragments and backend API calls.
 * This class is not exposed to the consuming application.
 */

import type {
  Fragment,
  FragmentMessage,
  CommandMessageType,
  CommandPayloadByType,
  TransactionSettings,
} from '@anddone/ajs-util';
import { createLogger } from '@anddone/ajs-logger';
import type { StateManagement } from './StateManagement';
import type { FragmentRegistry } from './fragment-registry/FragmentRegistry';
import {
  OrchestratorApiClient,
  OrchestratorCommandManager,
  OrchestratorLifecycleManager,
  OrchestratorMessageBus,
} from './orchestrator-module';

const logger = createLogger('SDK', 'Orchestrator');

export class Orchestrator {
  // TC39 private fields — inaccessible from outside this class at runtime,
  // preventing console or consuming-JS access to the command manager internals.
  readonly #state: StateManagement;
  readonly #fragmentRegistry: FragmentRegistry;
  readonly #commandManager: OrchestratorCommandManager;
  readonly #messageBus: OrchestratorMessageBus;
  readonly #lifecycleManager: OrchestratorLifecycleManager;
  readonly #apiClient = new OrchestratorApiClient();

  /**
   * constructor
   *
   * @param dataState - Shared config and SDK state
   * @param fragmentRegistry - Shared fragment registry
   */
  public constructor(dataState: StateManagement, fragmentRegistry: FragmentRegistry) {
    this.#state = dataState;
    this.#fragmentRegistry = fragmentRegistry;

    this.#commandManager = new OrchestratorCommandManager(this.#state, this.#fragmentRegistry);
    this.#messageBus = new OrchestratorMessageBus(this.#state, this.#commandManager);
    this.#lifecycleManager = new OrchestratorLifecycleManager(
      this.#messageBus,
      this.#commandManager,
      this.#fragmentRegistry,
    );

    logger.info('init', 'Creating orchestrator', {
      paymentIntentId: dataState.config.paymentIntentId,
      sessionId: dataState.session.id,
      allowedFragmentOrigin: this.#state.config.allowedFragmentOrigin,
    });

    this.#messageBus.initializeMessageListener();
  }

  //--------------  exposed public actions -------------------

  /**
   * Set the app initialized state
   *
   * @param initialized - boolean indicating if app is initialized
   * @returns void
   */
  public setSdkInitialized(initialized: boolean): void {
    this.#lifecycleManager.setSdkInitialized(initialized);
  }

  /**
   * Get the app initialized state
   *
   * @returns boolean - true if app is initialized, false otherwise
   */
  public getSdkInitialized(): boolean {
    return this.#lifecycleManager.getSdkInitialized();
  }

  /**
   * Set the reference to the orchestrator fragment.
   *
   * Kept as a compatibility pass-through. New code should use the shared
   * FragmentRegistry directly.
   *
   * @param fragment - Fragment instance representing the orchestrator fragment
   * @returns void
   */
  public setOrchestratorFragment(fragment: Fragment): void {
    this.#fragmentRegistry.setOrchestrator(fragment);
  }

  /**
   * Instruct the orchestrator to flush all queued commands for a fragment.
   *
   * @param fragment - Fragment instance representing the target fragment
   * @returns void
   */
  public flushPendingCommands(fragment: Fragment): void {
    this.#commandManager.flushPendingCommands(fragment);
  }

  /**
   * Instruct the orchestrator to clear all queued commands for a fragment.
   *
   * @param fragment - Fragment instance representing the target fragment
   * @returns void
   */
  public clearPendingCommandsForFragment(fragment: Fragment): void {
    this.#commandManager.clearPendingCommandsForFragment(fragment);
  }

  /**
   * Register a callback for receiving messages from fragments
   *
   * @param callback - Function to call when a message is received
   * @returns void
   */
  public onMessage(callback: (message: FragmentMessage) => void): void {
    this.#messageBus.onMessage(callback);
  }

  /**
   * Remove a message callback
   *
   * @param callback - Function to remove from listeners
   * @returns void
   */
  public offMessage(callback: (message: FragmentMessage) => void): void {
    this.#messageBus.offMessage(callback);
  }

  /**
   * Send a command to a specific fragment.
   *
   * @param fragment - Fragment instance to send the command to
   * @param type - Command message type
   * @param data - Data to include with the command
   * @returns void
   */
  public sendCommandToFragment<Type extends CommandMessageType>(
    fragment: Fragment,
    type: Type,
    data: CommandPayloadByType[Type],
  ): void {
    this.#commandManager.sendCommandToFragment(fragment, type, data);
  }

  /**
   * Helper function to send postMessage log to Orchestrator Fragment
   *
   * @param message - Log message string
   * @returns void
   */
  public sendLogCommandToOrchestratorFragment(message: string): void {
    this.#commandManager.sendLogCommandToOrchestratorFragment(message);
  }

  /**
   * Helper function to send fragment status updates to Orchestrator Fragment
   *
   * @returns void
   */
  public sendStatusUpdateCommandToOrchestratorFragment(): void {
    this.#commandManager.sendStatusUpdateCommandToOrchestratorFragment();
  }

  /**
   * Helper function to send transaction submit command to Orchestrator Fragment
   *
   * @returns void
   */
  public sendTransactionSubmitCommandToOrchestratorFragment(): void {
    this.#commandManager.sendTransactionSubmitCommandToOrchestratorFragment();
  }

  /**
   * Fetch merchant and payment intent settings from the settings endpoint.
   *
   * @returns Promise resolving to TransactionSettings
   */
  public fetchSettings(): Promise<TransactionSettings> {
    return this.#apiClient.fetchSettings(
      this.#state.config.hubURL,
      this.#state.config.appKey,
      this.#state.config.paymentIntentId,
    );
  }

  /**
   * Make an API call to the backend.
   *
   * @param endpoint - API endpoint to call
   * @param options - Fetch options
   * @returns Promise<TResponse> - Promise resolving to API response
   */
  public async apiCall<TResponse = unknown>(
    endpoint: string,
    options?: RequestInit,
  ): Promise<TResponse> {
    return this.#apiClient.apiCall<TResponse>(endpoint, options);
  }

  /**
   * Clean up and remove event listeners
   *
   * @returns void
   */
  public destroy(): void {
    this.#lifecycleManager.destroy();
  }
}

// Freeze the prototype to prevent external code from redefining methods on
// the Orchestrator class (e.g. via __proto__ or Object.getPrototypeOf) to
// intercept or bypass command sending.
Object.freeze(Orchestrator.prototype);
